import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  alertApiRef,
  createApiRef,
} from '@backstage/core-plugin-api';
import { ReactNode } from 'react';

export interface ToastApiMessage {
  title: ReactNode;
  description?: ReactNode;
  status?: 'neutral' | 'info' | 'success' | 'warning' | 'danger';
  links?: Array<{ label: string; href: string }>;
  timeout?: number;
}

export interface ToastApi {
  post(message: ToastApiMessage): { close(): void };
}

export const toastApiRef = createApiRef<ToastApi>({
  id: 'core.toast',
});

function reactNodeToString(node: ReactNode): string {
  if (node === null || node === undefined) {
    return '';
  }
  if (typeof node === 'string') {
    return node;
  }
  if (typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(reactNodeToString).join('');
  }
  if (typeof node === 'object' && 'props' in (node as any) && (node as any).props && 'children' in (node as any).props) {
    return reactNodeToString((node as any).props.children);
  }
  return '';
}

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  createApiFactory({
    api: toastApiRef,
    deps: { alertApi: alertApiRef },
    factory: ({ alertApi }) => {
      return {
        post(toast: ToastApiMessage) {
          const message = reactNodeToString(toast?.title);
          
          let severity: 'info' | 'success' | 'warning' | 'error' = 'info';
          if (toast?.status === 'success') severity = 'success';
          else if (toast?.status === 'warning') severity = 'warning';
          else if (toast?.status === 'danger') severity = 'error';
          else if (toast?.status === 'info') severity = 'info';

          alertApi.post({
            message,
            severity,
            display: toast?.timeout ? 'transient' : 'permanent',
          });

          return {
            close() {
              // No-op for legacy alertApi forwarding
            },
          };
        },
      };
    },
  }),
  ScmAuth.createDefaultApiFactory(),
];
